import React, { useMemo } from "react";
import { RowSelectionType } from "antd/es/table/interface";
import { Table } from "antd";

import { BaseEntity, BaseTableProps } from "./types";

const DEFAULT_ROW_SELECTION = {
  enabled: false,
  multiple: false,
  selectedRows: [],
  hideSelectAll: true,
  onSelect: () => {},
};

const BaseTable = <T extends BaseEntity>({
  dataSource,
  columns,
  pagination,
  onChange,
  rowSelection,
  ...props
}: BaseTableProps<T>) => {
  const currentRowSelection = rowSelection || DEFAULT_ROW_SELECTION;

  const rowSelectionConfig = useMemo(() => {
    if (!currentRowSelection?.enabled) return undefined;

    const currentSelectedRows = currentRowSelection.selectedRows || [];
    const type = (
      currentRowSelection.multiple ? "checkbox" : "radio"
    ) as RowSelectionType;

    return {
      type,
      onSelect: (record: T, selected: boolean) => {
        if (!selected) {
          currentRowSelection.onSelect(
            currentSelectedRows.filter((i) => i.id !== record.id) as T[],
          );
        } else {
          currentRowSelection.onSelect([
            ...(currentSelectedRows as T[]),
            record as T,
          ]);
        }
      },
      hideSelectAll: currentRowSelection.hideSelectAll ?? true,
      selectedRowKeys: currentSelectedRows.map((i) => i.id),
      onSelectMultiple: (selected: boolean, selectedRows: T[]) => {
        if (!selected) {
          const unselectedRowIds = selectedRows.map((i) => i.id);
          currentRowSelection.onSelect(
            currentSelectedRows.filter(
              (i) => !unselectedRowIds.includes(i.id),
            ) as T[],
          );
        } else {
          currentRowSelection.onSelect([
            ...(currentSelectedRows as T[]),
            ...(selectedRows as T[]),
          ]);
        }
      },
      onChange: (_selectedRowKeys: React.Key[], selectedRows: T[]) => {
        const currentPageIds = new Set(dataSource?.map((r) => r.id) || []);
        const otherPageSelections = currentSelectedRows.filter(
          (row) => !currentPageIds.has(row.id),
        );

        currentRowSelection.onSelect([...otherPageSelections, ...selectedRows]);
      },
    };
  }, [currentRowSelection, dataSource]);

  return (
    <Table<T>
      columns={columns}
      dataSource={dataSource}
      rowSelection={rowSelectionConfig}
      pagination={
        pagination && {
          ...pagination,
          showSizeChanger: true,
          position: ["bottomRight"],
          className:
            "custom-paginatio flex justify-end items-center full-widthn",
        }
      }
      onChange={onChange}
      scroll={{ x: "max-content" }}
      {...props}
    />
  );
};

export default BaseTable;
